import { useMemo, useRef } from 'react';
import { wallLayerOn } from '@tsr/shared-types';
import {
  MapView,
  type SectorController,
  type EvacuationOrderController,
  type CommunityController,
  type FloodInundationController,
} from './MapView';
import type { FlyController } from './cameraFly';
import { fireResponseTypeFilter, fireStageFilter } from './prefs';
import { WALL_WMS_OVERLAYS, wallWmsVisibility } from './layers/wmsOverlays';
import type { WallTourLayerDelta } from './wallTour';
import { deriveWallIncidentLayerProps, type WallIncidentContext } from './wallIncidentArtifacts';

/**
 * Wall MapView wrapper (#188; v2 #269; v3 #272) — the wall's single MapView:
 * the centre Common Operating Picture HERO.
 *
 * v3 (design-council PART XI Ruling 1, BINDING): the wall STOPS inheriting
 * `DEFAULT_VIEW_LAYERS`. Its layers DERIVE from the founder-locked
 * `WALL_VIEW_LAYERS` (`wallLayerOn`, shared-types; pinned by
 * `wallViewLock.test.ts`) — the wall is ACTIVE-SITUATION-FORWARD:
 *   - NO camera field (cameras live in the media flanks — pictures, not
 *     dots); with the green field gone, hero map motion collapses to
 *     red-only (Ruling 1.8) — a still hero is law, not a defect.
 *   - Extreme Weather Warnings JOIN, present at country zoom; the #153
 *     acked-CAP recede gate and the Law-3 acked country-drop LIFT wall-side
 *     (`liftAckedZoomGates`) so acked-live situations pin ACK-STEEL at every
 *     wall zoom — the wall structurally cannot false-quiet (Ruling 1.6).
 *   - GeoMet radar mounts as a wall WMS overlay (Ruling 1.4: α≤0.40,
 *     no-red-impersonation style, ~6-min refresh) — DEFAULT OFF pending the
 *     founder screenshot gate; the weather tour stop + `?radar=1` QA param
 *     can show it.
 *   - Per-operator prefs/mutes NEVER apply here (Ruling 1.7) — everything
 *     derives from the lock, not pref state.
 *   - #428 CONTEXT-TRIGGERED incident artifacts: when an incident is ESCALATED to
 *     the wall, THAT incident's evac ORDER/ALERT polygons + owned-incident framing
 *     un-suppress (the SEPARATE WALL_INCIDENT_ARTIFACT_LAYERS lock — NOT added to
 *     WALL_VIEW_LAYERS; derived from the escalation context, still never from prefs).
 *     Resolved + TTL-checked wall-side (WallView) → `incidentArtifacts`; the 3D
 *     flood model is in the lock set but its mount stays founder-gated.
 *
 * The hourly risk tour (Ruling 2) swaps the LAYER PROFILE ONLY via
 * `tourProfile` — the hero frame stays the locked Canada default.
 *
 * READ-ONLY (every select / authoring handler is a no-op) and auto-refreshing
 * on a wall cadence. Markers/labels scale up (`markerScale`) for wall-distance
 * readability (council Part V). This wraps the heavy ~60-prop MapView surface.
 */

const noop = (): void => undefined;
const EMPTY_SET: ReadonlySet<string> = new Set();
const EMPTY_STATUSES = {} as Record<string, never>;

export interface WallMapCounts {
  fire?: number;
  camera?: number;
  aircraft?: number;
  highRisk?: number;
  smoke?: number;
  ice?: number;
  flood?: number;
}

export interface WallMapProps {
  /** Marker/label emphasis for wall distance (council Part V "~2×"). */
  markerScale: number;
  /** Auto-refresh cadence (ms) so an unattended wall stays live. */
  autoRefreshMs: number;
  /** Bubbles layer counts up to the wall's count chips. */
  onCounts?: (counts: WallMapCounts) => void;
  /** Receives the FlyController so the wall can fly on a synced selection
   *  (BroadcastChannel). */
  flyControllerRef?: React.MutableRefObject<FlyController | null>;
  /** The active tour stop's layer-profile delta (Ruling 2.2), or null for the
   *  locked wall base profile. */
  tourProfile?: WallTourLayerDelta | null;
  /** `?radar=1` founder-gate QA override (Ruling 1.4e screenshot capture) —
   *  shows the radar overlay outside the weather tour stop. */
  radarGateOpen?: boolean;
  /** Self-refreshing WMS overlay passes (radar) — feeds the honest
   *  "RADAR · refreshed HH:MM" line. */
  onWmsOverlayRefresh?: (key: string, atMs: number) => void;
  /** #428 — the escalated incident's artifact scope (null = the base wall). When a
   *  `kind:'incident'` target is on the wall, un-suppresses THAT incident's evac
   *  polygons + owned-incident framing (context-triggered, PART XI Ruling 1 — never
   *  from operator prefs). Resolved + TTL-checked wall-side in WallView. */
  incidentArtifacts?: WallIncidentContext | null;
}

export function WallMap({
  markerScale,
  autoRefreshMs,
  onCounts,
  flyControllerRef,
  tourProfile = null,
  radarGateOpen = false,
  onWmsOverlayRefresh,
  incidentArtifacts = null,
}: WallMapProps): JSX.Element {
  // MapView populates these on mount; the wall never drives them (read-only), but
  // the props are required, so hold stable refs.
  const localFlyRef = useRef<FlyController | null>(null);
  const sectorControllerRef = useRef<SectorController | null>(null);
  const evacuationOrderControllerRef = useRef<EvacuationOrderController | null>(null);
  const communityControllerRef = useRef<CommunityController | null>(null);
  const floodInundationControllerRef = useRef<FloodInundationController | null>(null);
  const pulsingCameraIds = useRef<Set<string>>(new Set()).current;
  const counts = useRef<WallMapCounts>({}).current;

  const bump = (patch: WallMapCounts): void => {
    if (!onCounts) return;
    Object.assign(counts, patch);
    onCounts({ ...counts });
  };

  // HERO layers DERIVE from the WALL lock (PART XI Ruling 1 — wallViewLock
  // pins membership; no raw `true` lives here).
  const showCameras = wallLayerOn('live-video-feeds'); // false — Ruling 1.2
  const showLiveAircraft = wallLayerOn('aircraft');
  const showFireRisk = wallLayerOn('reported-oc-wildfires');
  const showHighRiskHotspots = wallLayerOn('urgent-anomalies');
  const showExtremeWeatherWarnings = wallLayerOn('extreme-weather-warnings'); // JOINS — Ruling 1.2
  const showProvincialBoundaries = wallLayerOn('provincial-boundaries');
  const showSectors = wallLayerOn('sectors');

  // The hourly tour's layer-profile delta (Ruling 2.2) — additive over the base.
  const fireAllStages = tourProfile?.allFireStages === true;
  const wmsVisibility = wallWmsVisibility([
    ...(tourProfile?.wmsOverlays ?? []),
    ...(radarGateOpen ? (['geomet-radar'] as const) : []),
  ]);

  // #428 CONTEXT-TRIGGERED incident artifacts (PART XI Ruling 1) — the escalated
  // incident's evac polygons + owned-incident framing, previously hard-coded off.
  // Derived SOLELY from the incident context (no operator prefs — Ruling 1.7): with
  // NO active incident every artifact is off (pinned by WallMap.test). The base
  // props are a frozen singleton, so a steady wall keeps a stable prop identity and
  // never re-runs MapView's apply-visibility effects.
  const artifacts = useMemo(
    () => deriveWallIncidentLayerProps(incidentArtifacts),
    [incidentArtifacts],
  );

  return (
    <MapView
      // ── Layers (derived from WALL_VIEW_LAYERS — the wall lock) ──────
      showCameras={showCameras}
      smokePriorityOnly={false}
      showLiveAircraft={showLiveAircraft}
      hiddenAircraftRoles={EMPTY_SET}
      showFireRisk={showFireRisk}
      // The fire tour stop widens to all stages for its dwell. Filters flow through
      // the SAME prefs helpers the COP uses (never an inline `['FUL']` — that re-narrows
      // to full-response and drops Ontario's monitored fires; see fireResponseTypeFilter).
      // The wall's two toggles map onto the helpers' WildfireLayerState.
      fireStages={
        fireStageFilter({
          allReportedWildfires: fireAllStages,
          reportedOcWildfires: showFireRisk,
        }) ?? undefined
      }
      fireResponseTypes={
        fireResponseTypeFilter({
          allReportedWildfires: fireAllStages,
          reportedOcWildfires: showFireRisk,
        }) ?? undefined
      }
      showHighRiskHotspots={showHighRiskHotspots}
      showExtremeWeatherWarnings={showExtremeWeatherWarnings}
      showProvincialBoundaries={showProvincialBoundaries}
      showSectors={showSectors}
      // #428 — the escalated incident's sectors (null on the base wall).
      sectorIncidentId={artifacts.sectorIncidentId}
      // Inert on the wall (Road Closures never mounts) — derived, not pref.
      roadClosuresRestrictions={wallLayerOn('ice-storm-road-closures-restrictions')}
      // ── PART XI wall profile ─────────────────────────────────────────
      // Ruling 1.3/1.6: acked-live anomalies + CAP polygons render at EVERY
      // wall zoom (ACK-STEEL); the COP's acked zoom gates lift wall-side only.
      liftAckedZoomGates={true}
      // Ruling 1.4: the GeoMet radar overlay (α≤0.40, pinned style, ~6-min
      // self-refresh) — default OFF behind the founder screenshot gate.
      extraWmsOverlays={WALL_WMS_OVERLAYS}
      wmsOverlayVisibility={wmsVisibility}
      onWmsOverlayRefresh={onWmsOverlayRefresh}
      // ── Tour stop deltas (Ruling 2.1) ────────────────────────────────
      showFwiHeatmap={tourProfile?.fwiHeatmap === true}
      showHydrometricGauges={tourProfile?.hydrometricGauges === true}
      showCommunities={tourProfile?.communities === true}
      // ── Everything else OFF (ambient Tier-2/3 + authoring) ──────────
      showSensors={false}
      showFireStations={false}
      showWildfireBases={false}
      showAviationBases={false}
      showOntarioCorps={false}
      hiddenPartners={EMPTY_SET}
      showRawHotspots={false}
      showFusionCandidates={false}
      showCameraSmoke={false}
      showPowerOutages={false}
      showRoadClosures={false}
      // #428 — bc-evacuation-orders (arcgis `evacuation` category) un-suppressed
      // for an escalated incident; {} on the base wall.
      arcgisCategoryVisibility={artifacts.arcgisCategoryVisibility}
      showCommsDegradationZones={false}
      // #428 — flood model is in the incident-artifact lock set but its Cesium mount
      // stays GATED behind the founder eyeball + on-wall caveat (WALL_FLOOD_MODEL_ENABLED).
      showFloodInundation={artifacts.showFloodInundation}
      viewshedOnSelect={false}
      viewshedCamera={null}
      showDroneCoverage={false}
      showStagingAreas={false}
      showAreaCommand={false}
      communityStatuses={EMPTY_STATUSES}
      communityVulnerabilityShading={false}
      // #428 — owned-incident scope (null on the base wall); scopes drone coverage,
      // the incident's own hazard marker already renders via the wall hazard layers.
      selectedIncidentPoint={artifacts.selectedIncidentPoint}
      // ── Wall profile knobs ──────────────────────────────────────────
      markerScale={markerScale}
      autoRefreshMs={autoRefreshMs}
      // ── Read-only: every selection / authoring handler is a no-op ────
      onSelectCamera={noop}
      onSelectCameraCluster={noop}
      onSelectIncidentCluster={noop}
      onSelectSensor={noop}
      onSelectAsset={noop}
      onSelectAircraft={noop}
      onSelectFire={noop}
      onSelectHighRiskHotspot={noop}
      onSelectRawHotspot={noop}
      onSelectCameraSmoke={noop}
      onSelectUtilityOutage={noop}
      onSelectRoadClosure={noop}
      onSelectArcgisFeature={noop}
      onSelectCommsDegradationZone={noop}
      onSelectCapAlert={noop}
      onSelectMarineVessel={noop}
      onSelectDroneCoverage={noop}
      onSelectStagingArea={noop}
      onSelectAreaCommand={noop}
      onSelectSector={noop}
      onSectorDrawn={noop}
      // #428 — operator-declared evac orders un-suppressed for an escalated incident.
      showOperatorEvacOrders={artifacts.showOperatorEvacOrders}
      onEvacOrderDrawn={noop}
      onSelectOperatorEvacOrder={noop}
      evacuationOrderControllerRef={evacuationOrderControllerRef}
      onSelectSmokeAnomaly={noop}
      onSelectIceAnomaly={noop}
      onSelectFloodAnomaly={noop}
      onSelectHydrometricGauge={noop}
      onSelectCommunity={noop}
      onCommunityPlaced={noop}
      // ── Counts → wall chips (hero only) ─────────────────────────────
      onCameraCountChange={(n) => bump({ camera: n })}
      onSensorCountChange={noop}
      onAssetCountsChange={noop}
      onAircraftCountChange={(n) => bump({ aircraft: n })}
      onFireCountChange={(n) => bump({ fire: n })}
      onHighRiskCountChange={(n) => bump({ highRisk: n })}
      onVesselCountChange={noop}
      onDroneCoverageCountChange={noop}
      onSmokeAnomalyCountChange={(n) => bump({ smoke: n })}
      onIceAnomalyCountChange={(n) => bump({ ice: n })}
      onFloodAnomalyCountChange={(n) => bump({ flood: n })}
      flyControllerRef={flyControllerRef ?? localFlyRef}
      sectorControllerRef={sectorControllerRef}
      communityControllerRef={communityControllerRef}
      floodInundationControllerRef={floodInundationControllerRef}
      pulsingCameraIds={pulsingCameraIds}
    />
  );
}
